import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, isNil, isEmpty } from 'lodash';
import { CheckCircle, AlertTriangle } from 'lucide-react';

import { Input, Select, FormRow, FormColumn, ComboBox } from 'components/Form';
import { Button, ActionButton } from 'components/Button';
import Validator from 'components/Validator';

import { unSupportedStates, states } from 'utils/states';
import { verifyAddress, searchAddress } from 'utils/verifyAddress';
import { toTitleCase } from 'utils/format';
import { zipMask } from 'utils/masks';

import SmartyStreetsPopup from './SmartyStreetsPopup';
import schema from './schema';

const doneTypingInterval = 500;

const AddressInput = ({
  label,
  initialValues,
  onSave,
  required = false,
  error,
  className = '',
  validator: { setValues, deleteErrors, errors, validate, values, onChangeHandler }
}) => {
  const [isAddressVerified, setIsAddressVerified] = useState(false);
  const [isAddressFirstLoad, setIsAddressFirstLoad] = useState(true);
  const [isAddressPopupVisible, setIsAddressPopupVisible] = useState(false);
  const [addressPopupType, setAddressPopupType] = useState(null);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const typingTimerRef = useRef(null);
  const streetAddressRef = useRef(null);
  const focusStreetOnEditRef = useRef(false);

  useEffect(() => {
    if (JSON.stringify(initialValues) === JSON.stringify(values)) return;

    if (!isNil(initialValues) && !isEmpty(initialValues) && !isNil(get(initialValues, 'streetAddress'))) {
      setValues({ ...initialValues });
      verifySmartyStreet({
        zipcode: get(initialValues, 'zip'),
        address: get(initialValues, 'streetAddress'),
        city: get(initialValues, 'city'),
        state: get(initialValues, 'state'),
      });
    }
  }, [initialValues]);

  useEffect(() => {
    if (isEditing && focusStreetOnEditRef.current) {
      focusStreetOnEditRef.current = false;
      requestAnimationFrame(() => {
        const input = streetAddressRef.current?.querySelector('input');
        if (input) input.focus();
      });
    }
  }, [isEditing]);

  const hasError = error && Object.keys(error).length > 0;

  const wrapperClasses = useMemo(() => {
    const base = `${label ? 'space-y-4' : ''} rounded-lg bg-slate-50 border transition-all duration-200 p-3`;

    if (hasError) {
      return `${base} ring-1 ring-red-300 border-red-300`;
    }
    
    if (isEditing) {
      return `${base} border-blue-300 ring-1 ring-blue pb-6`;
    }

    return `${base} border-slate-200 hover:border-gray-400 cursor-pointer focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none`;
  }, [hasError, isEditing]);

  const verifySmartyStreet = ({ address, city, state, zipcode }) => {
    setIsAddressLoading(true);

    verifyAddress({ zipcode, address, city, state: get(state, 'value') }).then((res) => {
      if (res) {
        if (get(res, 'metadata.recordType') === 'P') {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setIsAddressVerified(false);
          setAddressPopupType(3);
          setIsAddressFirstLoad(false);
        } else {
          const updatedValues = { ...values };
          const addressData = {
            ...updatedValues,
            streetAddress: get(res, 'deliveryLine1'),
            city: get(res, 'components.cityName'),
            state: states.find((state) => get(state, 'value') === get(res, 'components.state')),
            zip: `${get(res, 'deliveryPointBarcode')}`.slice(0, 5),
          };
          setIsAddressVerified(true);
          setIsAddressLoading(false);
          setIsAddressFirstLoad(false);
          setValues(addressData);
        }
      } else {
        setIsAddressPopupVisible(true);
        setIsAddressLoading(false);
        setIsAddressFirstLoad(false);
        setIsAddressVerified(false);
        setAddressPopupType(2);
      }
    }).catch(() => {
      setIsAddressLoading(false);
      setAddressPopupType(1);
      setIsAddressFirstLoad(false);
      setIsAddressVerified(false);
    });
  };

  const handleCloseAddressPopup = () => {
    setIsAddressPopupVisible(false);
  };

  const handleChange = (field, value) => {
    if (isAddressVerified) setIsAddressVerified(false);

    setValues({ ...values, [field]: value })
  };

  const onKeyUpHandler = () => {
    clearTimeout(typingTimerRef.current);
    setIsSuggestionLoading(true);
    typingTimerRef.current = setTimeout(doneTyping, doneTypingInterval);
  };

  const onKeyDownHandler = () => {
    clearTimeout(typingTimerRef.current);
  };

  const doneTyping = async () => {
    const { streetAddress } = values;
    if (!streetAddress || streetAddress.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSuggestionLoading(false);
      return;
    }
    
    try {
      const results = await searchAddress({ address: streetAddress });
      setSuggestions(results || []);
      if (results && results.length > 0) {
        setShowSuggestions(true);
      }
    } catch (e) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setIsSuggestionLoading(false);
  };

  const onSuggestionClick = (suggestion) => {
    const updatedValues = { ...values };
    updatedValues.streetAddress = `${suggestion.streetLine} ${suggestion.secondary || ''}`.trim();
    updatedValues.city = suggestion.city;
    updatedValues.state = states.find(state => state.value === suggestion.state);
    updatedValues.zip = suggestion.zipcode;
    setShowSuggestions(false);
    setSuggestions([]);

    if (unSupportedStates.indexOf(suggestion.state) !== -1) {
      setValues(updatedValues);
      setIsAddressVerified(false);
      setIsAddressPopupVisible(true);
      setIsAddressLoading(false);
      setAddressPopupType(4);
      return;
    }

    setIsAddressVerified(true);
    setValues(updatedValues);
    onSave(updatedValues);
    setIsEditing(false);
  };

  const handleAddressFocus = () => {
    if (suggestions && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleCancel = () => {
    setValues({ ...initialValues });
    deleteErrors(['streetAddress', 'city', 'state', 'zip']);
    setIsEditing(false);
  }

  const handleSave = () => {
    if (!validate().isValid) return;

    if(!isAddressVerified) {
      verifySmartyStreet({
        zipcode: get(values, 'zip'),
        address: get(values, 'streetAddress'),
        city: get(values, 'city'),
        state: get(values, 'state'),
      });
    }
    onSave(values);
    setIsEditing(false);
  }

  const handleBlur = (field) => (e) => {
    e && e.preventDefault();
    switch (field) {
      case 'state':
        if (unSupportedStates.indexOf(get(values, 'state.value')) !== -1) {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setAddressPopupType(4);
        } else if (isAddressFirstLoad && get(values, 'city') && get(values, 'streetAddress')) {
          verifySmartyStreet({
            zipcode: get(values, 'zip'),
            address: get(values, 'streetAddress'),
            city: get(values, 'city'),
            state: get(values, 'state'),
          });
        }
        break;
      case 'zip':
        if (isAddressFirstLoad && get(values, 'city') && get(values, 'state') && get(values, 'streetAddress')) {
          verifySmartyStreet({
            zipcode: e.target.value,
            address: get(values, 'streetAddress'),
            city: get(values, 'city'),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(field, toTitleCase(get(values, 'zip')));
        break;
      case 'streetAddress':
        if (isAddressFirstLoad && get(values, 'city') && get(values, 'state')) {
          verifySmartyStreet({
            zipcode: get(values, 'zip'),
            address: toTitleCase(e.target.value),
            city: get(values, 'city'),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(field, toTitleCase(get(values, 'streetAddress')));
        break;
      case 'city':
        if (isAddressFirstLoad && get(values, 'streetAddress') && get(values, 'state')) {
          verifySmartyStreet({
            zipcode: get(values, 'zip'),
            address: get(values, 'streetAddress'),
            city: toTitleCase(e.target.value),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(field, toTitleCase(get(values, 'city')));
        break;
      default:
        onChangeHandler(e.target.name, toTitleCase(e.target.value));
    }
  };

  const hasAddressValue = useMemo(() => {
    return values && Object.values(values).filter(el => el).length > 0;
  }, [values]);

  const formattedAddress = useMemo(() => {
    if (!hasAddressValue) return null;

    const parts = [
      values.streetAddress,
      values.city,
      get(values, 'state.label'),
      values.zip,
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : '-';
  }, [values, hasAddressValue]);

  return (
    <div className={`flex flex-col gap-2 ${className} relative`}>
      <div
        className={wrapperClasses}
        onClick={!isEditing ? () => setIsEditing(true) : undefined}
        onKeyDown={!isEditing ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsEditing(true); } } : undefined}
        onFocus={!isEditing ? () => { focusStreetOnEditRef.current = true; setIsEditing(true); } : undefined}
        tabIndex={!isEditing ? 0 : undefined}
        role={!isEditing ? 'button' : undefined}
        aria-label={!isEditing ? 'Edit address' : undefined}
      >
        <div className="flex items-center justify-between">

          <label
            className={`
              block text-xs uppercase font-bold transition-all duration-200
              ${hasError ? 'text-red-500' : 'text-slate-500'}
            `}
          >
            {label}
            {label && required && <span className="text-red-500 ml-1">*</span>}
          </label>

          {!isEditing && hasAddressValue && (
            isAddressVerified
              ? <CheckCircle size={18} className="text-blue-500" />
              : <div className="flex items-center gap-1 text-yellow-500">
                  <AlertTriangle size={18} />
                  <span className="text-xs font-medium">Not Verified</span>
                </div>
          )}

          {isEditing && (
            <ActionButton
              variant="cancel"
              onClick={handleCancel}
            />
          )}
        </div>

        {!isEditing && (
          hasAddressValue
            ? <p className="text-sm text-slate-700">{formattedAddress}</p>
            : <p className="text-sm text-gray-400">Click here to edit.</p>
        )}

        {isEditing && (
          <>
            <FormRow>
              <FormColumn className="relative">
                <div ref={streetAddressRef}>
                <ComboBox
                  label="Street Address"
                  required
                  value={values.streetAddress || ''}
                  onChange={(e) => handleChange('streetAddress', e.target.value)}
                  onKeyUp={onKeyUpHandler}
                  onKeyDown={onKeyDownHandler}
                  onFocus={handleAddressFocus}
                  onBlur={handleBlur('streetAddress')}
                  suggestions={suggestions}
                  isLoading={isSuggestionLoading}
                  onSuggestionClick={onSuggestionClick}
                  showSuggestions={showSuggestions}
                  placeholder="Street"
                  error={errors.streetAddress}
                />
                </div>
              </FormColumn>
            </FormRow>
            
            <FormRow>
              <FormColumn>
                <Input
                  label="City"
                  placeholder="City"
                  value={values.city || ''}
                  onBlur={handleBlur('city')}
                  onChange={(e) => handleChange('city', e.target.value)}
                  error={errors.city}
                  required
                />
              </FormColumn>
              <FormColumn>
                <Select
                  label="State"
                  placeholder="State"
                  value={values.state}
                  onBlur={handleBlur('state')}
                  onChange={(val) => handleChange('state', val)}
                  options={states}
                  error={errors.state}
                  required
                />
              </FormColumn>
              <FormColumn>
                <Input
                  label="Zip Code"
                  placeholder="_____"
                  inputClassName="font-numeric"
                  value={values.zip || ''}
                  onBlur={handleBlur('zip')}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  error={errors.zip}
                  mask={zipMask}
                  required
                  inputMode="numeric"
                />
              </FormColumn>
            </FormRow>

            {isAddressPopupVisible && (
              <SmartyStreetsPopup
                handleAbort={handleCloseAddressPopup}
                type={addressPopupType}
                data={{
                  address: get(values, 'streetAddress'),
                  city: get(values, 'city'),
                  state: get(values, 'state.value'),
                  zipcode: get(values, 'zip'),
                }}
              />
            )}

            <Button
              label="Save"
              modifier="primary"
              onClick={handleSave}
              isLoading={isAddressLoading}
              className="w-full mt-2 text-sm !py-3"
            />
          </>
        )}
      </div>

      {error && error !== 'The input field is required' && <p className="text-xs text-red-500 px-2">{error}</p>}
    </div>
  );
};

AddressInput.propTypes = {
  label: PropTypes.string.isRequired,
  values: PropTypes.shape({
    street: PropTypes.string,
    zip: PropTypes.string,
    state: PropTypes.string,
    city: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  validator: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
};

AddressInput.defaultProps = {
  required: false,
  error: null,
  className: '',
  initialValues: {},
};

export default Validator(schema)(AddressInput);
